import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToSection } from "@/App";

const links = [
  { label: "Work", target: "#work", testid: "nav-link-work" },
  { label: "Manifesto", target: "#manifesto", testid: "nav-link-manifesto" },
  { label: "About", target: "#about", testid: "nav-link-about" },
  { label: "Why Us", target: "#why-us", testid: "nav-link-why-us" },
  { label: "Contact", target: "#contact", testid: "nav-link-contact" },
];

const Wordmark = ({ onClick, testid, onLight = false }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="flex flex-col items-start group"
  >
    <span className="flex items-baseline">
      <span
        className={`font-logo font-bold text-2xl md:text-3xl leading-none group-hover:-rotate-6 transition-transform duration-300 inline-block ${
          onLight ? "text-black" : "text-signal"
        }`}
      >
        L
      </span>
      <span
        className={`font-logo font-bold text-2xl md:text-3xl leading-none -ml-0.5 ${
          onLight ? "text-neutral-900" : "text-white"
        }`}
      >
        Creative Studio
      </span>
    </span>
    <span
      className={`font-body text-[9px] md:text-[10px] tracking-[0.3em] uppercase mt-1.5 ${
        onLight ? "text-black/60" : "text-signal"
      }`}
    >
      Web Design — Melbourne
    </span>
  </button>
);

export default function Header() {
  const [open, setOpen] = useState(false);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) window.__lenis?.stop();
    else window.__lenis?.start();
  };

  const go = (target) => {
    setOpen(false);
    window.__lenis?.start();
    scrollToSection(target);
  };

  return (
    <>
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.6, ease: [0.76, 0, 0.24, 1] }}
        className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border"
        data-testid="site-header"
      >
        <div className="flex items-start md:items-center justify-between px-6 md:px-12 py-4 md:py-5">
          <Wordmark testid="nav-logo-button" onClick={() => scrollToSection("#top")} />
          <nav className="hidden md:flex items-center gap-10" data-testid="main-nav">
            {links.map((link) => (
              <button
                key={link.label}
                data-testid={link.testid}
                onClick={() => scrollToSection(link.target)}
                className="group relative text-sm tracking-[0.15em] uppercase text-white/70 hover:text-white transition-colors duration-300"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-white transition-[width] duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>
          <button
            className="md:hidden text-white p-1"
            onClick={toggle}
            data-testid="mobile-menu-button"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-background flex flex-col md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-start justify-between px-6 py-6">
              <Wordmark testid="mobile-menu-logo-button" onClick={() => go("#top")} />
              <button
                onClick={toggle}
                aria-label="Close menu"
                data-testid="mobile-menu-close-button"
                className="text-foreground p-1"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-6 gap-3" data-testid="mobile-menu-nav">
              {links.map((link, i) => (
                <div key={link.label} className="overflow-hidden">
                  <motion.button
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    exit={{ y: "110%" }}
                    transition={{ duration: 0.6, delay: 0.1 + i * 0.08, ease: [0.76, 0, 0.24, 1] }}
                    onClick={() => go(link.target)}
                    data-testid={`mobile-${link.testid}`}
                    className="flex items-baseline gap-4 font-display font-semibold text-5xl tracking-tighter py-2 text-left"
                  >
                    <span className="font-editorial italic font-medium text-signal text-xl">
                      0{i + 1}
                    </span>
                    {link.label}
                  </motion.button>
                </div>
              ))}
            </nav>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="px-6 pb-10 text-xs tracking-[0.2em] uppercase text-muted-foreground"
            >
              Melbourne, Australia — © 2026
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
